/** The `Label: value` row used by the detail cards. */

import React from 'react';
import { Box, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material';
import { fontFamilyMono } from '../../theme/theme';

export interface InfoRowProps {
  /** The colon is added here, so pass `Customer`, not `Customer:`. */
  label: string;
  /** A string or number is styled for you; a node is rendered as-is. */
  value: React.ReactNode;
  /** Monospace the value. Machine identifiers only — see `fontFamilyMono`. */
  mono?: boolean;
  /** Semantic colour for the value, e.g. `success.main`. */
  color?: TypographyProps['color'];
  /** Trailing control, e.g. an edit button. */
  action?: React.ReactNode;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, mono, color, action }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 2
    }}>
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        flexShrink: 0
      }}>
      {label}:
    </Typography>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0
      }}>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography
          variant="body2"
          sx={{
            color,
            textAlign: 'right',
            wordBreak: 'break-all',
            ...(mono && { fontFamily: fontFamilyMono }),
          }}
        >
          {value}
        </Typography>
      ) : (
        value
      )}
      {action}
    </Box>
  </Box>
);

// Memoised: the detail cards own their editors' state, so every keystroke
// re-renders all ~37 rows, and most take only primitives. Rows passing an
// `action` element still re-render, as they did before.
export default React.memo(InfoRow);
